import classNames from 'classnames';
import type { ReactNode } from 'react';
import { defineMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LandingPromoBanner } from '@sorare/core/src/components/Season2026/LandingPromoBanner';
import { LogoBezelMarquee } from '@sorare/core/src/components/Season2026/LogoBezelMarquee';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Play } from '@sorare/core/src/components/landing/Season2026/Play';
import { Scout } from '@sorare/core/src/components/landing/Season2026/Scout';
import type { Player } from '@sorare/core/src/components/landing/Season2026/WalletLandingHero';
import { WalletLandingHero } from '@sorare/core/src/components/landing/Season2026/WalletLandingHero';
import type { RewardMarqueeAsset } from '@sorare/core/src/components/landing/Season2026/Win';
import { Win } from '@sorare/core/src/components/landing/Season2026/Win';
import type {
  League,
  Pictures,
} from '@sorare/core/src/components/landing/types';
import { glossary } from '@sorare/core/src/lib/glossary';
import { NBATeams } from '@sorare/core/src/lib/nbaTeams';

import { FAQ } from '../FAQ';

const Root = styled(Vertical)`
  background: var(--c-black);
  padding: var(--double-unit) 0;
  &.showFAQ {
    padding-bottom: calc(8 * var(--unit));
  }
`;

type Assets = {
  heroAssets: {
    background: Pictures;
    players: Player[];
  };
  scoutAssets: {
    background: Pictures;
    players: Pictures;
  };
  playAssets: {
    background: Pictures;
    lineup: Pictures;
  };
  winAssets: RewardMarqueeAsset[];
};

type Props = {
  leaderboards: So5LeaderboardType[];
  showFAQ?: boolean;
  assets: Assets;
  leagueLogo?: ReactNode;
  league: League;
};

const heroMessages = {
  hero: glossary.landingHero,
  heroWallet: glossary.landingHeroWallet,
  heroWalletPlaceholder: glossary.landingHeroWalletPlaceholder,
  heroDescription: defineMessage({
    id: 'Nba.landing.WalletLanding.heroDescription',
    defaultMessage:
      'Build your 5-player lineups. When they shine on the court, you earn rewards!',
  }),
};

const scoutMessages = defineMessages({
  scout: {
    id: 'landing.Season2026.scout.title',
    defaultMessage: 'Scout like a pro',
  },
  scoutDescription: {
    id: 'Nba.landing.Season2026.scout.description.licensedClubs',
    defaultMessage: 'Every team. Every player.',
  },
  scoutButton: {
    id: 'landing.Season2026.scout.button',
    defaultMessage: 'Create your team',
  },
});

const playMessages = defineMessages({
  play: {
    id: 'landing.season26.Play.title',
    defaultMessage: 'Build your lineup',
  },
  playDescription: {
    id: 'Nba.landing.season26.Play.description.selectPlayers',
    defaultMessage:
      'Pick your best five each Game Week. When they ball, you win.',
  },
});

const winMessages = defineMessages({
  win: {
    id: 'landing.season26.Win.title',
    defaultMessage: 'Win Epic Rewards',
  },
  winDescription: {
    id: 'Nba.landing.season26.Win.description',
    defaultMessage:
      'Tickets, VIP experiences, cash or ETH - win it all! You may even score tickets to NBA All-Star Weekend!',
  },
});

export const WalletLanding = ({
  leaderboards,
  showFAQ,
  assets,
  leagueLogo,
  league,
}: Props) => {
  return (
    <Root
      gap={8}
      className={classNames({ extraBottomPadding: !showFAQ }, 'dark-mode')}
    >
      <Vertical>
        <Container>
          <LandingPromoBanner league={league} />
        </Container>
        <section>
          <Vertical gap={4}>
            <WalletLandingHero
              assets={assets.heroAssets}
              leagueLogo={leagueLogo}
              league={league}
              messages={heroMessages}
            />
            <LogoBezelMarquee
              leagues={NBATeams.map(({ fullName, svgUrl }) => ({
                displayName: fullName,
                pictureSecondaryUrl: svgUrl,
              }))}
            />
          </Vertical>
        </section>
      </Vertical>
      <section>
        <Scout
          assets={assets.scoutAssets}
          league={league}
          messages={scoutMessages}
        />
      </section>
      <section>
        <Play
          assets={assets.playAssets}
          league={league}
          messages={playMessages}
        />
      </section>
      <section>
        <Win
          league={league}
          assets={assets.winAssets}
          messages={{
            ...winMessages,
            marquee: heroMessages.hero,
          }}
        />
      </section>
      <section style={{ marginTop: 'calc(var(--unit) * -20)' }}>
        <CommunityBlock
          leaderboards={leaderboards.map(leaderboardType => ({
            sport: Sport.FOOTBALL,
            leaderboardType,
          }))}
        />
      </section>
      <section>
        <Container>
          <Ambassadors />
        </Container>
      </section>
      {!!showFAQ && <FAQ />}
    </Root>
  );
};
